import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Box, TableCell, TableRow, TableSortLabel, Theme } from '@mui/material';
import classNames from 'classnames';
import { ArrowUp } from 'lucide-react';
import React from 'react';
import HideColumnIcon from '../../../../../icons/HideColumnIcon';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import useTeceoStyles from '../../../../../useTeceoStyles';
import { HideColumnsTestIds } from '../../../../../utils/testIds/HideColumns.testIds';
import useFilter from '../../../../Crud/useFilter';
import useListOrder, { getMuiOrderDirection } from '../../../../Hooks/useListOrder';
import Tooltip from '../../../../Tooltip';
import { Column, VirtualizedTableHeaderAction } from '../VirtualizedTable.types';
import VirtualizedTableHeaderActionButton from '../VirtualizedTableHeaderActionButton';
import { useTableScrollerContext } from '../scroller/useTableScroller';
import {
  CHECKBOX_COLUMN_WIDTH,
  FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX,
  FIRST_COLUMN_PADDING_WITH_CHECKBOX,
  useStyles,
} from '../virtualizedTable.styles';
import { getDefaultOrderFromColumns } from '../virtualizedTable.utils';
import ColumnTitleWithTooltip from './components/ColumnTitleWithTooltip';
export interface VirtualizedTableFixedHeaderProps<T> {
  columns: Column<T>[];
  hasRowActions: boolean;
  headerAction?: VirtualizedTableHeaderAction;
  hasRowCheckbox: boolean;
  onToggleSelectAll?: () => void;
  isAllSelected?: boolean;
  onClickHideColumn?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disableCheckBox?: boolean;
  hasExtraHeader: boolean;
}

const stylesToAlignFixedHeaderWithColumns = <T,>(column: Column<T>) => ({
  width: column.width ?? 'auto',
  minWidth: column.width ?? 'auto',
  maxWidth: column.width ?? 'auto',
});

const getFirstColumnPaddingStyle = (theme: Theme, hasRowCheckbox: boolean) => ({
  paddingLeft: hasRowCheckbox
    ? theme.spacing(FIRST_COLUMN_PADDING_WITH_CHECKBOX)
    : theme.spacing(FIRST_COLUMN_PADDING_WITHOUT_CHECKBOX),
});

const VirtualizedTableFixedHeader = <T,>({
  columns,
  hasRowActions,
  headerAction,
  hasRowCheckbox,
  onToggleSelectAll,
  isAllSelected,
  onClickHideColumn,
  disableCheckBox,
  hasExtraHeader,
}: VirtualizedTableFixedHeaderProps<T>) => {
  const { classes } = useStyles();
  const { classes: teceoStyles } = useTeceoStyles();
  const { translate } = useTranslation();
  const { isScrolling } = useTableScrollerContext();

  const filter = useFilter();
  const listOrder = useListOrder({
    addFilters: filter.addFilters,
    defaultOrder: getDefaultOrderFromColumns(columns),
  });

  const hasStickyColumn = columns.some((c) => c.fixedHorizontalScroll);

  return (
    <TableRow>
      {!!onToggleSelectAll && !!hasRowCheckbox && (
        <TableCell
          padding="none"
          className={classNames({
            [classes.checkbox]: true,
            [classes.headerCell]: true,
            [classes.withExtraHeader]: hasExtraHeader,
            [classes.stickyCheckbox]: hasStickyColumn,
          })}
        >
          <Checkbox
            className={classes.disabledCheckbox}
            checked={isAllSelected}
            size="small"
            onChange={onToggleSelectAll}
            disabled={disableCheckBox}
          />
        </TableCell>
      )}
      {columns.map((column, index) => {
        const isFirstColumn = index === 0;
        const orderBy = column.orderByName ?? column.name;
        const cellClassName = classNames({
          [classes.headerCell]: true,
          [classes.withExtraHeader]: hasExtraHeader,
          [classes.fixedCell]: column.fixedHorizontalScroll,
          [classes.fixedScrolledCell]: column.fixedHorizontalScroll && isScrolling,
          [classes.fixedRightCell]: column.fixedRight,
          [classes.fixedRightScrolledCell]: column.fixedRight && isScrolling,
        });
        const cellSx = (theme: Theme) => ({
          ...stylesToAlignFixedHeaderWithColumns(column),
          ...(isFirstColumn && getFirstColumnPaddingStyle(theme, hasRowCheckbox)),
          ...(column.fixedHorizontalScroll && hasRowCheckbox && { left: CHECKBOX_COLUMN_WIDTH }),
        });

        if (column.sortable) {
          return (
            <TableCell
              key={column.name}
              sortDirection={getMuiOrderDirection('asc') || false}
              className={cellClassName}
              sx={cellSx}
            >
              <Box
                sx={(theme) => ({
                  minWidth: column.width ?? 'auto',
                  columnGap: theme.spacing(1),
                  display: 'flex',
                  alignItems: 'center',
                })}
              >
                <TableSortLabel
                  color="textSecondary"
                  IconComponent={ArrowUp}
                  active={!!listOrder.orders[orderBy]}
                  direction={getMuiOrderDirection(listOrder.orders[orderBy]) || 'asc'}
                  onClick={() => listOrder.handleRequestSort(orderBy)}
                  className={classes.sortLabel}
                >
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                    })}
                  >
                    <ColumnTitleWithTooltip
                      title={column.title}
                      translate={translate}
                      {...column.tooltipTitleProps}
                      className={classNames(
                        teceoStyles.truncateTwoLines,
                        teceoStyles.noTextTransform,
                      )}
                    />
                  </Box>
                </TableSortLabel>
              </Box>
            </TableCell>
          );
        }

        return (
          <TableCell key={column.name} className={cellClassName} sx={cellSx}>
            <Box
              onClick={column.onClickHeaderColumn}
              sx={{
                width: column.width ?? 'auto',
                minWidth: column.width ?? 'auto',
                maxWidth: column.width ?? 'auto',
                columnGap: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ColumnTitleWithTooltip
                title={column.title}
                translate={translate}
                {...column.tooltipTitleProps}
                className={classNames(teceoStyles.truncateTwoLines, teceoStyles.noTextTransform)}
              />
            </Box>
          </TableCell>
        );
      })}
      {onClickHideColumn && (
        <TableCell
          padding="checkbox"
          className={classNames({
            [classes.actionsColumn]: true,
            [classes.withExtraHeader]: hasExtraHeader,
          })}
          align="center"
        >
          <Tooltip title={translate(LangEnum.GLOBAL_CONFIGURE_COLUMNS)}>
            <IconButton
              size="medium"
              onClick={onClickHideColumn}
              className={classNames(classes.actionsIconButton, classes.hideColumnsButton)}
              testId={HideColumnsTestIds.HIDE_COLUMNS_BUTTON}
              icon={HideColumnIcon}
            />
          </Tooltip>
        </TableCell>
      )}
      {hasRowActions && !onClickHideColumn && (
        <TableCell
          padding="checkbox"
          className={classNames({
            [classes.actionsColumn]: true,
            [classes.withExtraHeader]: hasExtraHeader,
          })}
          align="center"
        >
          {headerAction && <VirtualizedTableHeaderActionButton action={headerAction} />}
        </TableCell>
      )}
    </TableRow>
  );
};

export default VirtualizedTableFixedHeader;
